import cloneDeep from 'lodash/cloneDeep'
import {
  createGiftEntityId,
  getSortedTiers,
  getTierFromAndAboveLabel,
  getTierRangeLabel,
} from './giftWithPurchaseConfigHelpers'
import { OUTLET_SHOP_ID } from '../outlet/outletCategoryConstants'
import { getPublicSiteUrl } from '../../../config/publicSite'
import {
  GIFT_FUND_CATALOG_SELECTION_KIND,
  createEmptyCatalogSelection,
  getCatalogSelectionFromItem,
  getGiftFundCatalogDisplayTitle,
  getGiftFundCatalogTypeSecondary,
  isGiftFundCatalogPoolDraft,
  validateGiftFundCatalogSelection,
} from './giftFundCatalogPool'

export const GIFT_FUND_ITEM_SCHEMA_VERSION = 1
export const GIFT_FUND_ITEM_SCHEMA_VERSION_POOL = 2
export const GIFT_FUND_SOURCE_CUSTOM = 'custom'
export const GIFT_FUND_SOURCE_CATALOG = 'catalog'

export const GIFT_FUND_CATALOG_FILTERS = {
  catalogSources: ['premium', 'outlet'],
  brandIds: [],
  categoryIds: null,
  subcategoryIds: null,
}

export const GIFT_FUND_PRODUCT_CODE_PREFIX = 'GWP-'

/** Унікальний код окремої позиції фонду (не плутаємо з barcode каси). */
export const generateGiftFundProductCode = () => {
  const stamp = Date.now().toString(36).toUpperCase()
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase()
  return `${GIFT_FUND_PRODUCT_CODE_PREFIX}${stamp}${rand}`
}

export const normalizeGiftFundProductCode = (value) => String(value || '').trim()

export const createBlankGiftFundItem = ({
  sourceType = GIFT_FUND_SOURCE_CATALOG,
  rewardGroupId = null,
  availableFromTierId = null,
} = {}) => ({
  id: createGiftEntityId('fund'),
  schemaVersion: GIFT_FUND_ITEM_SCHEMA_VERSION,
  sourceType,
  rewardGroupId,
  availableFromTierId,
  isActive: false,
  isArchived: false,
  productCode: sourceType === GIFT_FUND_SOURCE_CUSTOM ? generateGiftFundProductCode() : '',
  title: '',
  brandName: '',
  volumeText: '',
  description: '',
  specifications: {},
  photos: [],
  quantity: 0,
  catalogProductId: null,
  catalogSource: null,
  catalogTitle: null,
  catalogBarcode: null,
  catalogBrandName: null,
  catalogAvatarFileName: null,
  catalogPrice: null,
  catalogDescriptionShort: null,
  catalogIsActive: null,
  catalogIsArchived: null,
  catalogQuantity: null,
  catalogUnavailable: false,
  catalogSelection: createEmptyCatalogSelection(),
})

export const cloneGiftFundItem = (item) => cloneDeep(item)

export const getActiveGiftFundItems = (items = []) => items.filter((item) => item.isArchived !== true)

export const getArchivedGiftFundItems = (items = []) => items.filter((item) => item.isArchived === true)

export const countFundItemsByRewardGroupId = (items = [], groupId) =>
  items.filter((item) => item.rewardGroupId === groupId).length

export const countFundItemsByAvailableFromTierId = (items = [], tierId) =>
  items.filter((item) => item.availableFromTierId === tierId).length

export const GIFT_FUND_UNKNOWN_TIER_ID = '__unknown_tier__'

/** Групи для таблиць фонду: усі налаштовані ступені + позиції без ступені. */
export const groupGiftFundItemsByAvailableFromTier = (items = [], sortedTiers = []) => {
  const buckets = new Map(sortedTiers.map((tier) => [tier.id, []]))
  const unknown = []

  items.forEach((item) => {
    const tierId = item?.availableFromTierId
    if (buckets.has(tierId)) {
      buckets.get(tierId).push(item)
    } else {
      unknown.push(item)
    }
  })

  const groups = sortedTiers.map((tier) => ({
    id: tier.id,
    tier,
    title: getTierRangeLabel(tier, sortedTiers),
    items: buckets.get(tier.id) || [],
  }))

  if (unknown.length > 0) {
    groups.push({
      id: GIFT_FUND_UNKNOWN_TIER_ID,
      tier: null,
      title: 'Без ступені',
      items: unknown,
    })
  }

  return groups
}

export const buildFundGroupSelectOptions = (groups = []) =>
  groups.map((group) => ({
    value: group.id,
    label: group.name || '—',
  }))

export const buildFundTierSelectOptions = (tiers = []) =>
  getSortedTiers(tiers).map((tier) => ({
    value: tier.id,
    label: getTierFromAndAboveLabel(tier),
  }))

export const getFundItemDisplayTitle = (item) => {
  if (!item) {
    return '—'
  }

  if (item.sourceType === GIFT_FUND_SOURCE_CATALOG) {
    return getGiftFundCatalogDisplayTitle(item)
  }

  return String(item.title || '').trim() || '—'
}

/** Тип позиції для таблиці: Власна / Із каталогу. */
export const getFundTypeLabel = (item) => {
  if (!item) {
    return '—'
  }

  if (item.sourceType === GIFT_FUND_SOURCE_CUSTOM) {
    return 'Власна позиція'
  }

  if (item.sourceType === GIFT_FUND_SOURCE_CATALOG) {
    return 'Із каталогу'
  }

  return '—'
}

/** Premium / Outlet / Категорія / N товарів для другої строки типу. */
export const getFundCatalogSourceLabel = (item) => {
  if (!item || item.sourceType !== GIFT_FUND_SOURCE_CATALOG) {
    return null
  }

  return getGiftFundCatalogTypeSecondary(item)
}

/** @deprecated use getFundTypeLabel */
export const getFundSourceLabel = (item) => getFundTypeLabel(item)

export const getFundItemProductCode = (item) => {
  if (!item) {
    return '—'
  }

  if (item.sourceType === GIFT_FUND_SOURCE_CUSTOM) {
    return normalizeGiftFundProductCode(item.productCode) || '—'
  }

  if (isGiftFundCatalogPoolDraft(item)) {
    return '—'
  }

  const barcode = String(item.catalogBarcode || '').trim()
  return barcode || '—'
}

export const getFundItemBrandLabel = (item) => {
  if (!item) {
    return ''
  }

  if (item.sourceType === GIFT_FUND_SOURCE_CATALOG) {
    const selection = getCatalogSelectionFromItem(item)
    if (selection.kind === GIFT_FUND_CATALOG_SELECTION_KIND.brand) {
      return selection.brand?.brandName || ''
    }
    if (isGiftFundCatalogPoolDraft(item)) {
      return ''
    }
    return String(item.catalogBrandName || '').trim()
  }

  return String(item.brandName || '').trim()
}

export const getFundItemVolumeLabel = (item) => {
  if (!item || item.sourceType !== GIFT_FUND_SOURCE_CUSTOM) {
    return ''
  }

  return String(item.volumeText || '').trim()
}

export const getFundItemSecondaryMeta = (item) => {
  const parts = [getFundItemBrandLabel(item), getFundItemVolumeLabel(item)].filter(Boolean)
  return parts.join(' · ')
}

/** Число для сортування; null = «—» у UI (невідомий залишок каталогу). */
export const getFundItemRemainValue = (item) => {
  if (!item) {
    return null
  }

  if (item.sourceType === GIFT_FUND_SOURCE_CUSTOM) {
    return Number.isInteger(item.quantity) ? item.quantity : 0
  }

  if (item.catalogUnavailable) {
    return null
  }

  if (item.catalogQuantity == null || !Number.isFinite(Number(item.catalogQuantity))) {
    return null
  }

  return Number(item.catalogQuantity)
}

export const getFundItemQuantityLabel = (item) => {
  const value = getFundItemRemainValue(item)
  return value == null ? '—' : String(value)
}

export const getCatalogProductSiteUrl = ({ productId, catalogSource }) => {
  if (!productId) {
    return null
  }

  const base = getPublicSiteUrl()
  const path = catalogSource === 'outlet' ? `/outlet/${productId}` : `/products/${productId}`
  return `${base}${path}`
}

/**
 * Залишки лежать у shops[].remains[{ productId, quantity }], не в документі товару.
 * Premium — сума по всіх магазинах крім outlet; outlet — лише OUTLET_SHOP_ID.
 * Якщо запису немає — 0 (не «—»).
 */
export const sumProductRemainQuantityFromShops = (productId, shops = [], catalogSource) => {
  if (!productId || !Array.isArray(shops)) {
    return 0
  }

  if (catalogSource === 'outlet') {
    const outletShop = shops.find((shop) => String(shop.id) === String(OUTLET_SHOP_ID))
    const row = (outletShop?.remains || []).find((item) => item?.productId === productId)
    return Number(row?.quantity) || 0
  }

  return shops
    .filter((shop) => String(shop.id) !== String(OUTLET_SHOP_ID))
    .reduce((sum, shop) => {
      const row = (shop?.remains || []).find((item) => item?.productId === productId)
      return sum + (Number(row?.quantity) || 0)
    }, 0)
}

/**
 * Залишок для UI картки каталогу.
 * Пріоритет: shops → product.remains (якщо вже змерджено) → product.quantity → 0.
 */
export const getProductRemainQuantity = (product, { shops = null, catalogSource } = {}) => {
  if (!product) {
    return null
  }

  if (Array.isArray(shops)) {
    return sumProductRemainQuantityFromShops(product.id, shops, catalogSource)
  }

  if (Array.isArray(product.remains)) {
    return product.remains.reduce((sum, row) => sum + (Number(row?.quantity) || 0), 0)
  }

  if (Number.isFinite(Number(product.quantity))) {
    return Number(product.quantity)
  }

  return 0
}

export const getProductAvatarFileName = (product) => {
  const photos = Array.isArray(product?.photos) ? product.photos : []
  const avatar = photos.find((photo) => photo?.isAvatar) || photos[0]
  return avatar?.fileName || null
}

export const getCatalogProductImageUrl = ({ productId, fileName, catalogSource }) => {
  if (!productId || !fileName) {
    return null
  }

  const folder = catalogSource === 'outlet' ? 'outletProducts' : 'products'
  return `https://storage.googleapis.com/amecos.firebasestorage.app/${folder}/${productId}/thumbs/${fileName}_300x300.webp`
}

export const getFundItemAvatarImageUrl = (item) => {
  if (!item) {
    return null
  }

  if (item.sourceType === GIFT_FUND_SOURCE_CATALOG) {
    return getCatalogProductImageUrl({
      productId: item.catalogProductId,
      fileName: item.catalogAvatarFileName,
      catalogSource: item.catalogSource,
    })
  }

  const photos = Array.isArray(item.photos) ? item.photos : []
  const avatar = photos.find((photo) => photo?.isAvatar) || photos[0]
  if (!avatar) {
    return null
  }

  if (avatar.imageUrl) {
    return avatar.imageUrl
  }

  if (avatar.publicUrl) {
    return avatar.publicUrl
  }

  return null
}

export const mapCatalogProductToFundFields = (
  product,
  catalogSource,
  { brandName = null, shops = null, remainQuantity } = {}
) => {
  if (!product) {
    return {
      catalogProductId: null,
      catalogSource,
      catalogTitle: null,
      catalogBarcode: null,
      catalogBrandName: null,
      catalogAvatarFileName: null,
      catalogPrice: null,
      catalogDescriptionShort: null,
      catalogIsActive: null,
      catalogIsArchived: null,
      catalogQuantity: null,
      catalogUnavailable: true,
    }
  }

  const catalogQuantity =
    remainQuantity != null
      ? Number(remainQuantity) || 0
      : getProductRemainQuantity(product, { shops, catalogSource })

  return {
    catalogProductId: product.id,
    catalogSource,
    catalogTitle: product.name?.trim() || product.id,
    catalogBarcode: product.barcode?.trim() || null,
    catalogBrandName: brandName || product.brandName?.trim() || null,
    catalogAvatarFileName: getProductAvatarFileName(product),
    catalogPrice: product.price != null ? Number(product.price) : null,
    catalogDescriptionShort: product.descriptionShort?.trim() || null,
    catalogIsActive: product.isActive === true,
    catalogIsArchived: product.isArchived === true,
    catalogQuantity,
    catalogUnavailable: false,
  }
}

export const hasLocalFundPhotos = (photos = []) =>
  Array.isArray(photos) && photos.some((photo) => photo && (photo.imageUrl || photo.publicUrl || photo.fileName))

export const emptyCatalogFundFields = () => ({
  catalogProductId: null,
  catalogSource: null,
  catalogTitle: null,
  catalogBarcode: null,
  catalogBrandName: null,
  catalogAvatarFileName: null,
  catalogPrice: null,
  catalogDescriptionShort: null,
  catalogIsActive: null,
  catalogIsArchived: null,
  catalogQuantity: null,
  catalogUnavailable: false,
  catalogSelection: null,
})

export const emptyCustomFundFields = () => ({
  productCode: '',
  title: '',
  brandName: '',
  volumeText: '',
  description: '',
  specifications: {},
  photos: [],
  quantity: 0,
})

/** У збережену позицію потрапляють лише спільні поля + поля обраного типу. */
export const buildGiftFundItemForSave = (draft) => {
  const isCatalogPool = isGiftFundCatalogPoolDraft(draft)
  const schemaVersion =
    isCatalogPool || Number(draft.schemaVersion) >= GIFT_FUND_ITEM_SCHEMA_VERSION_POOL
      ? GIFT_FUND_ITEM_SCHEMA_VERSION_POOL
      : GIFT_FUND_ITEM_SCHEMA_VERSION

  const base = {
    id: draft.id,
    schemaVersion,
    sourceType: draft.sourceType,
    rewardGroupId: draft.rewardGroupId ?? null,
    availableFromTierId: draft.availableFromTierId ?? null,
    isActive: draft.isActive === true,
    isArchived: draft.isArchived === true,
  }

  if (draft.sourceType === GIFT_FUND_SOURCE_CUSTOM) {
    const linkedCatalogProductId = String(draft.catalogProductId || '').trim() || null
    return {
      ...base,
      schemaVersion: GIFT_FUND_ITEM_SCHEMA_VERSION,
      productCode: normalizeGiftFundProductCode(draft.productCode),
      title: String(draft.title || '').trim(),
      brandName: String(draft.brandName || '').trim(),
      volumeText: String(draft.volumeText || '').trim(),
      description: draft.description || '',
      specifications: draft.specifications && typeof draft.specifications === 'object' ? draft.specifications : {},
      photos: Array.isArray(draft.photos) ? draft.photos : [],
      quantity: Number.isInteger(draft.quantity) ? draft.quantity : 0,
      ...emptyCatalogFundFields(),
      catalogProductId: linkedCatalogProductId,
      catalogSource: linkedCatalogProductId
        ? draft.catalogSource === 'outlet'
          ? 'outlet'
          : 'premium'
        : null,
      catalogTitle: linkedCatalogProductId ? String(draft.catalogTitle || '').trim() || null : null,
      catalogBrandName: linkedCatalogProductId
        ? String(draft.catalogBrandName || '').trim() || null
        : null,
    }
  }

  const selection = getCatalogSelectionFromItem(draft)
  const primary = selection.productRefs[0]
  const isSingleSku = selection.kind === GIFT_FUND_CATALOG_SELECTION_KIND.products && selection.productRefs.length === 1

  return {
    ...base,
    ...emptyCustomFundFields(),
    catalogProductId: isSingleSku ? primary.id : null,
    catalogSource:
      selection.kind === GIFT_FUND_CATALOG_SELECTION_KIND.brand ||
      selection.kind === GIFT_FUND_CATALOG_SELECTION_KIND.category
        ? 'premium'
        : isSingleSku
          ? primary.source
          : draft.catalogSource || primary?.source || null,
    catalogTitle: isSingleSku
      ? draft.catalogTitle || primary.title || primary.id
      : selection.kind === GIFT_FUND_CATALOG_SELECTION_KIND.brand
        ? selection.brand?.brandName || null
        : selection.kind === GIFT_FUND_CATALOG_SELECTION_KIND.category
          ? selection.category?.pathLabel || selection.category?.catalogTaxonomyNodeName || null
          : draft.catalogTitle || null,
    catalogBarcode: isSingleSku ? draft.catalogBarcode || primary.barcode : null,
    catalogBrandName: isSingleSku
      ? draft.catalogBrandName || primary.brandName || null
      : selection.kind === GIFT_FUND_CATALOG_SELECTION_KIND.brand
        ? selection.brand?.brandName || null
        : draft.catalogBrandName || null,
    catalogAvatarFileName: isSingleSku ? draft.catalogAvatarFileName || null : null,
    catalogPrice: isSingleSku && draft.catalogPrice != null ? draft.catalogPrice : null,
    catalogDescriptionShort: isSingleSku ? draft.catalogDescriptionShort || null : null,
    catalogIsActive: isSingleSku ? draft.catalogIsActive ?? null : null,
    catalogIsArchived: isSingleSku ? draft.catalogIsArchived ?? null : null,
    catalogQuantity: isSingleSku && draft.catalogQuantity != null ? draft.catalogQuantity : null,
    catalogUnavailable: isSingleSku ? draft.catalogUnavailable === true : false,
    catalogSelection: selection,
  }
}

/** Локальна перевірка перед додаванням у список 2А. */
export const validateGiftFundItemDraft = (
  item,
  { rewardGroups = [], tiers = [], existingItems = [] } = {}
) => {
  const errors = []

  if (!item?.sourceType || ![GIFT_FUND_SOURCE_CUSTOM, GIFT_FUND_SOURCE_CATALOG].includes(item.sourceType)) {
    errors.push('Оберіть тип позиції')
  }

  if (!item?.rewardGroupId || !rewardGroups.some((group) => group.id === item.rewardGroupId)) {
    errors.push('Оберіть групу винагороди')
  }

  if (!item?.availableFromTierId || !tiers.some((tier) => tier.id === item.availableFromTierId)) {
    errors.push('Оберіть ступінь доступності')
  }

  if (item?.sourceType === GIFT_FUND_SOURCE_CUSTOM) {
    const productCode = normalizeGiftFundProductCode(item.productCode)

    if (!productCode) {
      errors.push('Вкажіть код товару')
    } else {
      const duplicate = existingItems.some(
        (row) =>
          row.id !== item.id &&
          row.sourceType === GIFT_FUND_SOURCE_CUSTOM &&
          normalizeGiftFundProductCode(row.productCode).toLowerCase() === productCode.toLowerCase()
      )
      if (duplicate) {
        errors.push('Код товару вже використовується в фонді')
      }
    }

    if (!String(item.title || '').trim()) {
      errors.push('Вкажіть назву')
    }

    if (!Number.isInteger(item.quantity) || item.quantity < 0) {
      errors.push('Кількість має бути цілим числом ≥ 0')
    }

    if (item.isActive === true && !hasLocalFundPhotos(item.photos)) {
      errors.push('Для активної окремої позиції потрібне фото')
    }
  }

  if (item?.sourceType === GIFT_FUND_SOURCE_CATALOG) {
    errors.push(...validateGiftFundCatalogSelection(item).errors)
  }

  return {
    ok: errors.length === 0,
    errors: [...new Set(errors)],
  }
}
